import { options } from 'preact';

/**
 * Sono stato costretto a creare questo tool perché sono cretino e ho iniziato il frontend con il piede sbagliato.
 * Prima preact aggiornava tutti i componenti figli, ed era opt-out.
 * Pra aggiornare un componente è opt-in tramite segnali e useState.
 */

let currentComponent: any = null;
const proxyMap = new WeakMap<object, object>();

// Invece di un Set globale, mappiamo ogni Oggetto al Set dei componenti che lo leggono
const objectSubscribers = new WeakMap<object, Set<any>>();

// Per pulire la memoria all'unmount, teniamo traccia di quali Set sta ascoltando ogni componente
const componentSubscriptions = new WeakMap<any, Set<Set<any>>>();

// Gestione batch degli update
const pendingUpdates = new Set<any>();
let isMicrotaskQueued = false;

// 1. HOOK INTERNO DI PREACT (__r = Render)
const internalOptions = options as any;
const oldRender = internalOptions.__r;
internalOptions.__r = (vnode: any) => {
    currentComponent = vnode.__c || vnode._component; 
    if (oldRender) oldRender(vnode);
};

// 2. HOOK PUBBLICO DI PREACT (diffed = Render completato)
const oldDiffed = options.diffed;
options.diffed = (vnode: any) => {
    currentComponent = null;
    if (oldDiffed) oldDiffed(vnode);
};

// 3. HOOK PUBBLICO DI PREACT (unmount)
const oldUnmount = options.unmount;
options.unmount = (vnode: any) => {
    const c = vnode.__c || vnode._component;
    if (c) {
        // Rimuove il componente da tutte le iscrizioni (Evita Memory Leaks!)
        const subs = componentSubscriptions.get(c);
        if (subs) {
            subs.forEach(set => set.delete(c));
            componentSubscriptions.delete(c);
        }
    }
    if (oldUnmount) oldUnmount(vnode);
};

// Funzione che accorpa e spara gli update SOLO ai componenti interessati
function queueComponentUpdate(c: any) {
    pendingUpdates.add(c);
    if (!isMicrotaskQueued) {
        isMicrotaskQueued = true;
        Promise.resolve().then(() => {
            pendingUpdates.forEach(comp => {
                if (typeof comp.forceUpdate === 'function') comp.forceUpdate();
                else if (typeof comp.setState === 'function') comp.setState({});
            });
            pendingUpdates.clear();
            isMicrotaskQueued = false;
        });
    }
}

// Esportazione del motore reattivo
export function makeReactive<T extends object>(target: T): T {
    if (target === null || typeof target !== 'object') return target;
    if (proxyMap.has(target)) return proxyMap.get(target) as T;
    if (target instanceof Date) return target;

    // Associa il componente corrente all'oggetto specifico che sta leggendo
    const trackAccess = () => {
        if (currentComponent) {
            let subs = objectSubscribers.get(target);
            if (!subs) {
                subs = new Set();
                objectSubscribers.set(target, subs);
            }
            subs.add(currentComponent);

            let compSubs = componentSubscriptions.get(currentComponent);
            if (!compSubs) {
                compSubs = new Set();
                componentSubscriptions.set(currentComponent, compSubs);
            }
            compSubs.add(subs);
        }
    };

    // Scatena l'aggiornamento solo per chi legge questo specifico oggetto
    const triggerUpdate = () => {
        const subs = objectSubscribers.get(target);
        if (subs) subs.forEach(c => queueComponentUpdate(c));
    };

    if (target instanceof Set || target instanceof Map) {
        const collectionProxy = new Proxy(target, {
            get(obj, prop) {
                trackAccess();
                const val = (obj as any)[prop];
                if (typeof val === 'function') {
                    if (['add', 'set', 'delete', 'clear'].includes(prop as string)) {
                        return function (...args: any[]) {
                            const res = val.apply(obj, args);
                            triggerUpdate();
                            return res;
                        };
                    }
                    return val.bind(obj);
                }
                return val;
            }
        });
        proxyMap.set(target, collectionProxy);
        return collectionProxy as T;
    }

    const proxy = new Proxy(target, {
        get(obj, prop, receiver) {
            if (prop === 'toJSON') {
                return () => obj;
            }
            trackAccess();
            const val = Reflect.get(obj, prop, receiver);
            return typeof val === 'object' && val !== null ? makeReactive(val) : val;
        },
        set(obj, prop, value, receiver) {
            const oldVal = Reflect.get(obj, prop, receiver);
            if (oldVal !== value) {
                Reflect.set(obj, prop, value, receiver);
                triggerUpdate();
            }
            return true;
        },
        deleteProperty(obj, prop) {
            if (Reflect.has(obj, prop)) {
                Reflect.deleteProperty(obj, prop);
                triggerUpdate();
            }
            return true;
        }
    });

    proxyMap.set(target, proxy);
    return proxy;
}